import { Button, Card, Sheet, TextStyle } from '@superops/ui-design-system';
import React, { FC, useCallback, useEffect, useState } from 'react';
import {
  COMMON_CONSTANTS,
  EventEmitter,
  useUserMSPCheckHook,
  withErrorBoundary
} from '@nile/components';
import ApproverListSheet from '@nile/mangifera/src/Containers/SheetContainers/ApproverListSheet.tsx';
import { GET_APPROVALS } from '@nile/graphql';
import { useQuery } from '@apollo/client';
import { isEmpty } from 'lodash';
import { SVGIcon } from '@superops/super-icons';
import ApprovalSheet from '@nile/mangifera/src/Containers/SheetContainers/ApprovalDetailsSheet.js';
import { FormattedMessage } from 'react-intl';

const Display = ({ icon, text, color, length }) => {
  return (
    <div className="flex g5">
      <div>
        <SVGIcon name={icon} color={color} />
      </div>
      <div>
        <TextStyle variation="subdued">
          {length} {text}
        </TextStyle>
      </div>
    </div>
  );
};

interface Client {
  name?: string;
  accountId?: string;
  logoFileName?: string | null;
  primaryContact?: string | null;
}

interface TicketApprovalCardProps {
  client: Client;
  ticketId: string;
  loadingComponent?: any;
  hideMoreInfo?: boolean;
}

const TicketApprovalCard: FC<TicketApprovalCardProps> = (props) => {
  const { client, ticketId, loadingComponent, hideMoreInfo = false } = props;
  const [sheetActive, setSheetActive] = useState(false);
  const [allApprovals, setAllApprovals] = useState(null);
  const [approvedData, setApprovedData] = useState(null);
  const [rejectedData, setRejectedData] = useState(null);
  const [moreInfoData, setMoreInfoData] = useState(null);
  const [cancelledData, setCancelledData] = useState(null);

  const isMSP = useUserMSPCheckHook();

  const [approvalSheetActive, setApprovalSheetActive] = useState(false);

  const updateApproversList = (data) => {
    if (data) {
      const approved = [];
      const rejected = [];
      const moreInfo = [];
      const cancelled = [];

      const currentApprovals = data.map((el) => {
        if (el?.status === 'Rejected') {
          rejected.push(el);
        }

        if (el?.status === 'Cancelled') {
          cancelled.push(el);
        }

        if (el?.status === 'Approved') {
          approved.push(el);
        }

        if (el?.status === 'Need more Information') {
          moreInfo.push(el);
        }
        return {
          ...el,
          id: el.userId,
          label: el.name
        };
      });

      setAllApprovals(currentApprovals);

      if (rejected.length) {
        setRejectedData(rejected);
      }
      if (approved.length) {
        setApprovedData(approved);
      }

      if (moreInfo.length) {
        setMoreInfoData(moreInfo);
      }

      if (cancelled.length) {
        setCancelledData(cancelled);
      }
    }
  };

  const {
    data: approvals,
    loading: approvalsLoading,
    error: approvalError,
    refetch
  } = useQuery(GET_APPROVALS, {
    fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NO_CACHE,
    variables: {
      listInfo: {
        condition: {
          joinOperator: 'AND',
          operands: [
            {
              attribute: 'ticket.ticketId',
              operator: 'is',
              value: ticketId
            }
          ]
        }
      }
    },
    onCompleted({ getApprovals }) {
      updateApproversList(getApprovals?.approvals);
    }
  });

  const refreshApprovals = () => {
    refetch().then((res) => {
      if (res?.data?.getApprovals?.approvals) {
        updateApproversList(res?.data?.getApprovals?.approvals);
      }
    });
  };

  useEffect(() => {
    EventEmitter.subscribe('approvalChange', refreshApprovals);
    return function cleanup() {
      EventEmitter.unsubscribe('approvalChange');
    };
  }, []);

  const toggleSheetActive = useCallback(
    () => setSheetActive((active) => !active),
    [sheetActive]
  );

  const onSendingApproval = () => {
    toggleSheetActive();
    refreshApprovals();
    EventEmitter.dispatch('refetchTicketApproval');
  };

  const onResendAndUpdateApproval = () => {
    toggleApprovalSheetActive();
    refreshApprovals();
  };

  const toggleApprovalSheetActive = useCallback(
    () => setApprovalSheetActive((active) => !active),
    [approvalSheetActive]
  );

  const LoadingComponent = loadingComponent || <></>;
  if (approvalsLoading && LoadingComponent) {
    return <>Loading...</>;
  }

  return (
    <div id="ticket-approval-activator">
      {isEmpty(allApprovals) ? (
        <>
          {isMSP ? (
            <Button plain onClick={toggleSheetActive}>
              <FormattedMessage id="ticket.details.insights.initiateApprovals" />
            </Button>
          ) : (
            <TextStyle>
              <FormattedMessage id="ticket.details.approval.empty.text" />
            </TextStyle>
          )}
        </>
      ) : (
        <>
          <Button plain onClick={toggleApprovalSheetActive}>
            {allApprovals?.length || 0} approvals sent
          </Button>

          {(approvedData || rejectedData || moreInfoData) && !hideMoreInfo && (
            <div className="flex g10 mt10">
              {approvedData && (
                <Display
                  icon="Check"
                  text={
                    <FormattedMessage id="ticket.details.runbook.approved" />
                  }
                  length={approvedData?.length || 0}
                  color={'#00A437'}
                />
              )}

              {rejectedData && (
                <Display
                  icon="RejectedCircle"
                  text={
                    <FormattedMessage id="ticket.details.runbook.rejected" />
                  }
                  length={rejectedData?.length || 0}
                  color={'#D72C0D'}
                />
              )}

              {moreInfoData && (
                <Display
                  icon="Conversation"
                  text={
                    <FormattedMessage id="ticket.details.approvalBubble.needMoreInfo" />
                  }
                  length={moreInfoData?.length || 0}
                  color={'#FF7F23'}
                />
              )}

              {cancelledData && (
                <Display
                  icon="Restrict"
                  text={
                    <FormattedMessage id="ticket.details.runbook.cancelled" />
                  }
                  length={cancelledData?.length || 0}
                  color={'##999999'}
                />
              )}
            </div>
          )}
        </>
      )}
      <Sheet open={sheetActive} onClose={toggleSheetActive}>
        <ApproverListSheet
          ticketId={ticketId}
          client={client}
          onSave={onSendingApproval}
          onCancel={toggleSheetActive}
        />
      </Sheet>

      <Sheet open={approvalSheetActive} onClose={toggleApprovalSheetActive}>
        <ApprovalSheet
          ticketId={ticketId}
          client={client}
          onCancel={toggleApprovalSheetActive}
          approvalList={allApprovals}
          onResendApproval={onResendAndUpdateApproval}
          onUpdateApproval={onResendAndUpdateApproval}
        />
      </Sheet>
    </div>
  );
};

export default withErrorBoundary(TicketApprovalCard);
